import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Search, UserPlus, X } from "lucide-react";
import api, { apiError } from "@/api";
import { toast } from "sonner";

export function CustomerSearch({ customers, value, onSelect, onCreated, testId = "order-customer" }) {
  const selected = customers.find((c) => c.id === value);
  const [q, setQ] = useState("");
  const [openList, setOpenList] = useState(false);
  const [hi, setHi] = useState(0);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const words = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return customers
      .filter((c) => words.every((w) => `${c.name} ${c.phone}`.toLowerCase().includes(w)))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 12);
  }, [customers, q]);

  const isPhone = /^\+?\d{6,}$/.test(q.trim());
  const pick = (c) => { onSelect(c.id); setQ(""); setOpenList(false); };
  const startCreate = () => {
    setForm({ name: isPhone ? "" : q.trim(), phone: isPhone ? q.trim() : "", address: "" });
    setCreating(true); setOpenList(false);
  };
  const onKey = (e) => {
    if (!openList && ["ArrowDown", "Enter"].includes(e.key)) { setOpenList(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[hi]) pick(results[hi]); else if (q.trim()) startCreate(); }
    else if (e.key === "Escape") setOpenList(false);
  };

  const saveNew = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Nama & nomor HP wajib diisi");
    setSaving(true);
    try {
      const { data } = await api.post("/customers", form);
      toast.success("Customer baru dibuat");
      await onCreated?.(data);
      onSelect(data.id); setCreating(false); setQ("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (creating) {
    return (
      <div data-testid="new-customer-form" className="rounded-md border border-sky-200 bg-sky-50/50 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-sky-800 flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Customer Baru</span>
          <button type="button" data-testid="new-customer-cancel" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Nama *</Label><Input data-testid="new-customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama customer" autoFocus /></div>
          <div><Label className="text-xs">No. HP *</Label><Input data-testid="new-customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxxx" /></div>
        </div>
        <div><Label className="text-xs">Alamat (opsional)</Label><Input data-testid="new-customer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Alamat pengiriman" /></div>
        <Button size="sm" data-testid="new-customer-save" onClick={saveNew} disabled={saving} className="bg-sky-700 hover:bg-sky-800">{saving ? "Menyimpan..." : "Simpan & Pilih"}</Button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <div ref={boxRef} className="relative flex-1 min-w-0">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input data-testid={testId} value={openList || !selected ? q : `${selected.name} · ${selected.phone}`}
            onChange={(e) => { setQ(e.target.value); setOpenList(true); setHi(0); }} onFocus={() => { setOpenList(true); setHi(0); }} onKeyDown={onKey}
            placeholder={selected ? `${selected.name} · ${selected.phone}` : "Ketik nama atau nomor HP customer..."} autoComplete="off" className={`pl-8 ${selected && !openList ? "font-medium text-slate-900" : ""}`} />
        </div>
        {openList && (
          <div data-testid={`${testId}-results`} className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-white shadow-lg text-sm">
            {results.map((c, idx) => (
              <button type="button" key={c.id} data-testid={`${testId}-opt-${c.phone}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c)} onMouseEnter={() => setHi(idx)}
                className={`w-full text-left px-3 py-1.5 flex items-center gap-2 ${idx === hi ? "bg-sky-50" : ""}`}>
                <span className="font-medium text-slate-900 truncate">{c.name}</span>
                <span className="ml-auto font-mono text-xs text-slate-500 shrink-0">{c.phone}</span>
              </button>
            ))}
            {results.length === 0 && q.trim() && (
              <button type="button" data-testid="create-customer-from-search" onMouseDown={(e) => e.preventDefault()} onClick={startCreate}
                className="w-full text-left px-3 py-2 flex items-center gap-2 text-sky-700 hover:bg-sky-50">
                <UserPlus className="w-4 h-4" /> Buat customer baru {isPhone ? `dengan nomor "${q.trim()}"` : `"${q.trim()}"`}
              </button>
            )}
            {results.length === 0 && !q.trim() && <div className="px-3 py-2 text-slate-400 text-xs">Belum ada customer</div>}
          </div>
        )}
      </div>
      <Button type="button" variant="outline" data-testid="btn-new-customer" onClick={startCreate} className="gap-1 shrink-0"><UserPlus className="w-4 h-4" /> Customer Baru</Button>
    </div>
  );
}
